import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'

import type { ReactElement } from 'react'
import type { ISwitchButton } from '~/components/Buttons/SwitchButton'
import type { IAppManagementByDeviceType } from '~/types/application-management'

import { getDeviceTypes, getDeviceTypeSwitchButtons } from '../common'

import ModalAppSelector, { AppSelectorDropdown } from '~/components/AppSelector'
import BackNavigation from '~/components/BackNavigation'
import SwitchButton from '~/components/Buttons/SwitchButton'
import { shortcut } from '~/const/shortcut'
import { useShortcut } from '~/hooks/useShortcut'
import { useAppSummary } from '~/providers/AppSummary'
import { useSearchParamsContext } from '~/providers/SearchParams'
import { getHeaderParams } from '~/utils/application'

import '../common.scss'
import './styles.scss'

interface Props {
  apps: IAppManagementByDeviceType[]
  displayAppSelector?: boolean
  displaySwitchButton?: boolean
  isAppSelectorOpen?: boolean
  isMulti?: boolean
  isSwitchDisabled: boolean
}

const HeaderClassic = ({
  apps,
  displayAppSelector = true,
  displaySwitchButton = true,
  isAppSelectorOpen = false,
  isMulti,
  isSwitchDisabled: defaultSwitchDisabled,
}: Props): ReactElement => {
  /** Local state */

  const location = useLocation()

  const app = !isMulti ? apps[0] : undefined

  const { dispatchToSearchParams } = useSearchParamsContext()

  const [isAppSelectorModalOpen, setIsAppSelectorModalOpen] = useState(isAppSelectorOpen)

  const [isSwitchDisabled, setIsSwitchDisabled] = useState(defaultSwitchDisabled)

  const [switchButtons, setSwitchButtons] = useState<ISwitchButton[]>([])

  const titleParams = useMemo(() => getHeaderParams(location.pathname), [location.pathname])

  /** Queries & Mutations */

  const { appDetails } = useAppSummary()

  /** Effects */

  const deviceTypes = useMemo(() => getDeviceTypes(appDetails), [appDetails])

  useEffect(() => {
    if (app) {
      setSwitchButtons(
        getDeviceTypeSwitchButtons({
          appKey: app.app_key,
          currentDeviceType: app.device_type,
          deviceTypes,
        })
      )
    }
  }, [app, deviceTypes])

  useEffect(() => {
    setIsSwitchDisabled(deviceTypes.length < 2)
  }, [deviceTypes])

  useEffect(() => {
    setIsAppSelectorModalOpen(isAppSelectorOpen)
  }, [isAppSelectorOpen])

  useShortcut({
    ...shortcut.openAppSelector,
    fn: () => {
      const modal = document.querySelector('.ReactModal__Body--open')
      if (!modal) {
        setIsAppSelectorModalOpen(true)
      }
    },
  })

  /** Handlers */

  const handleSwitchButtonSelectionChange = (buttonId: string) => {
    const selectedButton = switchButtons.find(({ id }) => id === buttonId)

    setSwitchButtons(
      switchButtons.map((button) => ({
        ...button,
        isActive: button.id === buttonId,
      }))
    )

    if (selectedButton?.application) {
      dispatchToSearchParams({ type: 'update', value: { app: selectedButton.application } })
    }
  }

  const handleOpenModal = (value: boolean) => () => setIsAppSelectorModalOpen(value)

  /** Render */

  return (
    // NOTE: this className is used in the LayoutWithFiler component
    <div className="header-component header-classic">
      {titleParams?.backNavigation ? (
        <BackNavigation title={titleParams.backNavigation.title} to={titleParams.backNavigation.to} />
      ) : null}
      <div className="app-filters">
        <AppSelectorDropdown apps={apps} displayAppLogo isMulti={isMulti} onClick={handleOpenModal(true)} />
        {displayAppSelector ? (
          <ModalAppSelector
            isMulti={isMulti}
            isOpen={isAppSelectorModalOpen}
            onClose={handleOpenModal(false)}
            selectedApps={apps.map(({ app_key, device_type }) => ({ app_key, device_type }))}
          />
        ) : null}
        {displaySwitchButton && apps.length ? (
          <SwitchButton
            backgroundColor="dark"
            buttons={switchButtons}
            className="device-switch-button"
            disabled={isSwitchDisabled}
            onButtonSelectionChange={handleSwitchButtonSelectionChange}
          />
        ) : null}
      </div>
    </div>
  )
}

export default HeaderClassic
